import { globalObservability } from '@opendoor/observability/slim';
import { ClientError } from 'graphql-request';
import { z } from 'zod';

import { impersonationSession } from '../account/impersonation';
import {
  SellerLeadCakeVariantDocument,
  SellerLeadCakeVariantEvaluateDocument,
} from './__generated__/sellerLeadCakeRollout.zeus';
import { requestZeusFromBrowser } from './zeusBrowserClient';

const Sentry = globalObservability.getSentryClient();
export const DECISION_TIMEOUT_MS = 1500;
const reportDecision = (message: string, reason: string) => {
  try {
    Sentry.captureMessage?.(message, { level: 'warning', tags: { reason } });
  } catch {
    // A telemetry failure must not change where the seller goes.
  }
};
const reportDecisionFailure = (reason: string) => {
  reportDecision('Seller lead rollout decision failed in Cosmos', reason);
};
type SellerLeadCakeDecisionOptions = { applicationId: string };
type SellerLeadCakeEvaluationOptions = SellerLeadCakeDecisionOptions & { signal: AbortSignal };

const evaluateSellerLeadCakeVariant = async ({
  applicationId,
  signal,
}: SellerLeadCakeEvaluationOptions): Promise<boolean> => {
  if (impersonationSession.isImpersonatingSession()) {
    const result = await requestZeusFromBrowser(
      SellerLeadCakeVariantDocument,
      { applicationId },
      signal,
    );
    return result.seller.application?.experience.experimentVariant?.value === 'treatment';
  }
  const result = await requestZeusFromBrowser(
    SellerLeadCakeVariantEvaluateDocument,
    { input: { applicationId, experiment: 'DASHBOARD_SELLER_QUESTIONNAIRE' } },
    signal,
  );
  const assignment = result.sellerApplicationExperimentVariantEvaluate;
  switch (assignment.__typename) {
    case 'SellerApplicationExperimentVariantEvaluatePayload':
      return assignment.assignment.value === 'treatment';
    case 'InvalidInputError':
    case 'NotFoundError':
    case 'UnauthorizedError':
      reportDecisionFailure(assignment.__typename);
      return false;
    default:
      assignment satisfies never;
      reportDecisionFailure('unknown');
      return false;
  }
};

const failureReason = (error: unknown): string => {
  if (error instanceof ClientError) return 'server';
  if (error instanceof TypeError) return 'network';
  return error instanceof Error ? error.name : 'unknown';
};

export const isSellerLeadCakeTreatment = async ({
  applicationId,
}: SellerLeadCakeDecisionOptions): Promise<boolean> => {
  if (!z.uuid().safeParse(applicationId).success) return false;
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<boolean>((resolve) => {
    timeout = setTimeout(() => {
      controller.abort();
      reportDecision('Seller lead rollout decision timed out in Cosmos', 'timeout');
      resolve(false);
    }, DECISION_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      evaluateSellerLeadCakeVariant({ applicationId, signal: controller.signal }).catch(
        (error: unknown) => {
          if (!controller.signal.aborted) {
            reportDecisionFailure(failureReason(error));
          }
          return false;
        },
      ),
      deadline,
    ]);
  } finally {
    clearTimeout(timeout);
    controller.abort();
  }
};
